import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useShowCommunityArt } from "@design-lab/shared/lib/config";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import type { MenuId } from "../../model/state";
import { MENU_IDS } from "../../model/state";
import { AIDesignTooltip } from "./AIDesignTooltip";
import { DailyCredits } from "./DailyCredits";
import { MenuItem } from "./MenuItem";
import { NextButton } from "./NextButton";
import { AiDesignIcon, CommunityArtIcon, LibraryIcon, UploadIcon } from "./icons";

const BASE_MENU_ITEMS = [
  {
    id: MENU_IDS.UPLOAD_IMPORT,
    icon: <UploadIcon className="size-6" />,
    label: "Upload/Import"
  },
  {
    id: MENU_IDS.AI_DESIGN,
    icon: <AiDesignIcon className="size-6" />,
    label: "AI Design"
  },
  {
    id: MENU_IDS.LIBRARY,
    icon: <LibraryIcon className="size-6" />,
    label: "Library"
  }
] as const;

const COMMUNITY_ART_ITEM = {
  id: MENU_IDS.COMMUNITY_ART,
  icon: <CommunityArtIcon className="size-6" />,
  label: "Community Art"
} as const;

type LeftMenuProps = {
  activeMenu: MenuId;
  onMenuChange: (menuId: MenuId) => void;
};

const AI_DESIGN_TOOLTIP_KEY = "design_lab_ai_design_tooltip_shown";

export function LeftMenu({ activeMenu, onMenuChange }: LeftMenuProps) {
  const shouldShowCommunityArt = useShowCommunityArt();
  const { isDesktop } = useResponsive();
  const [showAIDesignTooltip, setShowAIDesignTooltip] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const hasSeenTooltip = localStorage.getItem(AI_DESIGN_TOOLTIP_KEY);

    if (
      !hasSeenTooltip &&
      searchParams.get("design-menu") !== MENU_IDS.AI_DESIGN &&
      searchParams.get("artwork_urls") === null
    ) {
      setShowAIDesignTooltip(true);
      localStorage.setItem(AI_DESIGN_TOOLTIP_KEY, "true");
    }
  }, []);

  const handleCloseTooltip = () => {
    setShowAIDesignTooltip(false);
  };

  const menuItems = shouldShowCommunityArt
    ? [...BASE_MENU_ITEMS, COMMUNITY_ART_ITEM]
    : BASE_MENU_ITEMS;

  const renderMenuItems = () =>
    menuItems.map(item => {
      const isAIDesignItem = item.id === MENU_IDS.AI_DESIGN;

      const menuItem = (
        <MenuItem
          icon={item.icon}
          label={item.label}
          isActive={activeMenu === item.id}
          onClick={() => onMenuChange(item.id)}
          isAIDesign={isAIDesignItem}
        />
      );

      // Wrap AI Design with tooltip
      if (isAIDesignItem) {
        return (
          <AIDesignTooltip key={item.id} isOpen={showAIDesignTooltip} onClose={handleCloseTooltip}>
            {menuItem}
          </AIDesignTooltip>
        );
      }

      return <div key={item.id}>{menuItem}</div>;
    });

  if (isDesktop) {
    return (
      <div className="flex h-full flex-col bg-white">
        <div className="mb-6 mt-2">
          <h1 className="leading-7.5 text-xl font-semibold text-gray-900">Create Your Way</h1>
        </div>
        <div className="space-y-1">{renderMenuItems()}</div>
        {activeMenu === MENU_IDS.AI_DESIGN && (
          <div className="mt-8">
            <DailyCredits />
          </div>
        )}
        <div className="mt-auto">
          <NextButton />
        </div>
      </div>
    );
  }

  return (
    <div className="flex overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {renderMenuItems()}
    </div>
  );
}
